import React from 'react';
import env from '../../env';
import utils from '../../utils';

/**
 * BreadcrumbSchema Component
 * 
 * Implements Schema.org BreadcrumbList structured data for SEO.
 * Follows best practices as defined by Schema.org:
 * - https://schema.org/BreadcrumbList
 * 
 * Based on the cuisinart project implementation pattern.
 * 
 * @param {Object} props
 * @param {Object} props.breadcrumb - Breadcrumb data object
 * @param {Array} props.breadcrumb.items - Array of breadcrumb items
 * @param {string} props.breadcrumb.items[].title - Display text for the breadcrumb item
 * @param {string} props.breadcrumb.items[].link - URL path for the breadcrumb item (optional, relative or absolute)
 * 
 * @example
 * <BreadcrumbSchema breadcrumb={{
 *   items: [
 *     { title: 'Home', link: '/' },
 *     { title: 'Products', link: '/products' },
 *     { title: 'Hair Ties', link: null } // Current page, will use window.location.pathname
 *   ]
 * }} />
 */
export function BreadcrumbSchema({ breadcrumb }) {
  React.useEffect(() => {
    // Validate breadcrumb data
    if (!breadcrumb || !breadcrumb.items || !Array.isArray(breadcrumb.items) || breadcrumb.items.length === 0) {
      return;
    }

    // Remove any existing breadcrumb schema script
    const existingSchema = document.querySelector('script[data-breadcrumb-schema="true"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Create breadcrumb schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumb.items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.title,
        // Always include item property - use current page URL if link is not provided
        "item": item.link ? utils.site.fullUrl(item.link) : utils.site.fullUrl(window.location.pathname)
      }))
    };

    // Create and inject the schema script
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-breadcrumb-schema', 'true');
    schemaScript.textContent = JSON.stringify(breadcrumbSchema);
    
    // Inject into head for better detection
    document.head.appendChild(schemaScript);

    // Cleanup function to remove schema when component unmounts
    return () => {
      const existingSchema = document.querySelector('script[data-breadcrumb-schema="true"]');
      if (existingSchema) {
        existingSchema.remove();
      }
    };
  }, [breadcrumb]);

  return null;
}
